import { uniq } from 'lodash-es';
import type { FormError, FormErrorSeverity, FormRecord } from 'models/formError';

function toInvalidFieldIdWithSeverity<
  Form = FormRecord,
  Keys extends string = string,
  KeysPrefix extends string = string,
>(errors: readonly FormError<Form, Keys, KeysPrefix>[], severity?: FormErrorSeverity) {
  return uniq(
    errors.filter(x => x.severity === severity && !x.underlyingValidationErrors).flatMap(x => x.fields ?? []),
  );
}

export type InvalidFields<Form = FormRecord, Keys extends string = string, KeysPrefix extends string = string> =
  | LegacyInvalidFields
  | NextInvalidFields<Form, Keys, KeysPrefix>;

export function areLegacyInvalidFields(invalidFields: InvalidFields): invalidFields is LegacyInvalidFields {
  return invalidFields.type === 'legacy';
}

interface LegacyInvalidFields {
  type: 'legacy';
  errors: string[];
  warnings: string[];
}

type NextInvalidFields<Form = FormRecord, AllKeys extends string = string, KeysPrefix extends string = string> = Record<
  keyof Form,
  readonly FormError<Form, AllKeys, KeysPrefix>[]
>;

export function toLegacyInvalidFields<
  Form = FormRecord,
  AllKeys extends string = string,
  KeysPrefix extends string = string,
>(validationErrors: readonly FormError<Form, AllKeys, KeysPrefix>[]): LegacyInvalidFields {
  return {
    type: 'legacy',
    errors: toInvalidFieldIdWithSeverity<Form, AllKeys, KeysPrefix>(validationErrors, 'Error') as string[],
    warnings: toInvalidFieldIdWithSeverity<Form, AllKeys, KeysPrefix>(validationErrors, 'Warning') as string[],
  };
}

export function toInvalidFields<Form = FormRecord, AllKeys extends string = string, KeysPrefix extends string = string>(
  validationErrors: readonly FormError<Form, AllKeys, KeysPrefix>[],
): NextInvalidFields<Form, AllKeys, KeysPrefix> {
  return validationErrors
    .slice()
    .sort(
      (err1, err2) =>
        validationErrorToPriority<Form, AllKeys, KeysPrefix>(err1) -
        validationErrorToPriority<Form, AllKeys, KeysPrefix>(err2),
    )
    .reduce(
      (res, error) => {
        error.fields?.forEach(field => {
          if (!res[field]) {
            res[field] = [];
          }
          res[field] = [...res[field], error];
        });
        return res;
      },
      {} as NextInvalidFields<Form, AllKeys, KeysPrefix>,
    );
}

const validationErrorToPriority = <
  Form = FormRecord,
  AllKeys extends string = string,
  KeysPrefix extends string = string,
>(
  validationError: FormError<Form, AllKeys, KeysPrefix>,
): number => (validationError.severity === 'Error' ? 1 : 0);
